// F-4 — Real PDF export (blueprint task F-4). Client-side via the existing
// jspdf + html2canvas deps. Replaces the alert stub in
// CreateConstructionPlanPage.handleExportPDF.
//
// Document model (BINDING order): classification card + citations →
// personnel → stakeholders → lift details (per lift: card → shown-work
// block ADJACENT → diagram at scale w/ legend) → compliance steps →
// crane-lift-plan pages appended with the §7.3 cover note.
// Footer citation on every page: "Per ANSI/ASSP A10.48-2023" + refs (§6.3).
//
// buildPlanPdfSections is PURE (deterministic, no jsPDF imports) so it is
// unit-testable in jsdom; exportPlanPdf renders the model into jsPDF and
// captures each lift's live LiftDiagramPanel SVG via html2canvas.
import type { PlanDataV2, LiftDetail, LiftComputed, PlanAttachment } from '@/types/planData';
import { computeLift } from '@/utils/riggingCalculations.v2';
import type { ProofTestRecord } from '@/components/classification/ProofTestLog';

/** Structural guard for stored proof-test records (plan.proofTests is unknown[]). */
function isRecordLike(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

export const PDF_FOOTER_CITATION = 'Per ANSI/ASSP A10.48-2023';

export type PlanPdfSectionKind =
  | 'title' | 'classification' | 'personnel' | 'stakeholders' | 'lift'
  | 'shown-work' | 'diagram' | 'compliance' | 'proof-test' | 'attachment';

export interface PlanPdfSection {
  kind: PlanPdfSectionKind;
  heading: string;
  /** Key-value rows rendered as label: value lines. */
  lines: string[];
  /** For kind='diagram': the lift id whose live LiftDiagramPanel is captured. */
  liftId?: string;
}

/**
 * F-4 audit round 1 — recompute guard for the export path (CRITICAL fix).
 *
 * `readPlan` upgrades v1 payloads with `emptyComputed()` — status 'ok' with
 * ALL forces 0 — so any export that trusts `plan.liftDetails[i].computed`
 * prints "Load line tension: 0.0 lb" on a safety document. The invariant:
 * the PDF's forces must come from a FRESH `computeLift` at export time,
 * never from stored/migrated cache. A lift whose recompute refuses keeps
 * the refusal-bearing computed (the honest path — refusals printed, no
 * numbers), never zero-forces-under-ok.
 *
 * Pure + exported so the page→export path is unit-testable in jsdom.
 */
export function recomputePlanForExport(plan: PlanDataV2): PlanDataV2 {
  return {
    ...plan,
    liftDetails: plan.liftDetails.map((lift) => {
      let computed: LiftDetail['computed'];
      try {
        computed = computeLift(lift);
      } catch {
        computed = null;
      }
      // computeLift null (engine threw) ⇒ keep the stored cache rather than
      // printing a fabricated refusal; every documented refusal path returns
      // a refused computed rather than throwing, so null is unreachable in
      // practice and the stored cache is the least-wrong fallback.
      return computed ? { ...lift, computed } : lift;
    }),
  };
}

export interface PlanPdfInput {
  plan: PlanDataV2;
  /** Plan title for the cover header (defaults to projectInfo.projectName). */
  title?: string;
  /** Specific standard refs used — printed with the footer citation (§6.3). */
  refs?: string[];
}

const fmt = (n: number | null | undefined): string =>
  n == null || !Number.isFinite(n) ? '—' : `${n.toFixed(1)} lb`;

const SCENARIO_LABELS: Record<LiftDetail['scenario'], string> = {
  'S1-top-only': 'S1 — top block only',
  'S2-top-heel': 'S2 — top + heel block',
  'S3-jump': 'S3 — jump block',
  'S4-tied-back': 'S4 — tied back (tether catch)',
  'S5-redirect': 'S5 — redirect (A-4(a))',
  'S6-highline-trolley': 'S6 — highline + trolley (A-4(b))',
  custom: 'Custom',
};

function attachmentCoverNote(a: PlanAttachment): string {
  const meta = a.metadata ?? {};
  const rev = meta.revision ?? 'n/a';
  const date = meta.planDate ?? 'n/a';
  return (
    `Attached: ${meta.company ?? a.name} lift plan rev ${rev}, ${date} — ` +
    'crane operations per their plan; plan coordination per A10.48 §1.2/III(d)'
  );
}

function liftLines(lift: LiftDetail, computed: LiftComputed): string[] {
  const lines: string[] = [];
  lines.push(lift.name);
  lines.push(`Scenario: ${SCENARIO_LABELS[lift.scenario] ?? lift.scenario}`);
  if (lift.personnelLift) lines.push('PERSONNEL LIFT — advisory-only (§8.1)');
  lines.push(
    `Gross load: ${fmt(lift.loading.grossLoadLbs)} · DLF: ${lift.loading.dynamicFactor.toFixed(2)}` +
      ` · Parts of line: ${lift.hardware.blockParts}`
  );
  if (computed.status === 'refused') {
    lines.push('REFUSED — no computed forces (see refusal codes):');
    computed.refusals.forEach((r) => lines.push(`  ${r.rule}: ${r.message}`));
  } else {
    const f = computed.forces;
    lines.push('Computed forces (recomputed on export — never trusted from storage):');
    lines.push(`  Load line tension: ${fmt(f.linePartTensionLbs)}`);
    lines.push(`  Top block resultant: ${fmt(f.topBlockResultantLbs)}`);
    if (f.heelBlockLbs != null) lines.push(`  Heel block: ${fmt(f.heelBlockLbs)}`);
    if (f.jumpBlockLbs != null) lines.push(`  Jump block: ${fmt(f.jumpBlockLbs)}`);
    if (f.redirectBlockLbs != null) lines.push(`  Redirect block: ${fmt(f.redirectBlockLbs)}`);
    if (f.mountBlockLbs != null) lines.push(`  Mount block: ${fmt(f.mountBlockLbs)}`);
    if (f.tagLineLbs != null) lines.push(`  Tag line: ${fmt(f.tagLineLbs)}`);
    if (f.winchPullLbs != null) lines.push(`  Winch pull: ${fmt(f.winchPullLbs)}`);
    const r = f.structureReactions;
    lines.push(
      `  Structure reactions: top H ${fmt(r.topLateralLbs)} / V ${fmt(r.topVerticalLbs)} · base moment ${r.baseMomentFtLbs.toFixed(1)} ft·lb`
    );
  }
  lines.push(`Angles: θ ${computed.derivedAngles.loadPositionDeg.toFixed(2)}° from vertical`);
  if (lift.complianceRefs.length) lines.push(`Refs: ${lift.complianceRefs.join('; ')}`);
  if (lift.signOff.reviewedBy) lines.push(`Lift sign-off: ${lift.signOff.reviewedBy} at ${lift.signOff.at ?? '—'}`);
  return lines;
}

function shownWorkLines(computed: LiftComputed): string[] {
  if (computed.status === 'refused') return computed.refusals.map((r) => `${r.rule}: ${r.message}`);
  const lines: string[] = [];
  computed.working.forEach((w) => {
    lines.push(`${w.output}`);
    lines.push(`  Formula: ${w.formula}`);
    lines.push(`  Substitution: ${w.substitution}`);
    lines.push(`  Result: ${w.result}`);
    if (w.assumption) lines.push(`  Assumption: ${w.assumption}`);
  });
  return lines;
}

/** Legend: node labels rendered on the diagram figure (standard's CAPS callouts). */
const DIAGRAM_LEGEND = [
  'TOP BLOCK', 'HEEL BLOCK', 'JUMP BLOCK', 'IDLER/REDIRECT BLOCK', 'MOUNT BLOCK',
  'HOIST', 'LOAD', 'TAG LINE HOLD', 'TROLLEY BLOCK',
];

// ---------------------------------------------------------------------------
// Pure section builder (unit-tested in jsdom)
// ---------------------------------------------------------------------------
export function buildPlanPdfSections(input: PlanPdfInput): PlanPdfSection[] {
  const { plan } = input;
  const sections: PlanPdfSection[] = [];
  const projectInfo = plan.projectInfo ?? {};

  // 1 — Classification card + citations (cover info folded in — card is first)
  const cls = plan.classification;
  const clsLines: string[] = [
    String(projectInfo.projectName ?? input.title ?? 'Construction Plan'),
    `Project #: ${projectInfo.projectNumber ?? '—'}`,
    `Date: ${projectInfo.date ?? '—'}`,
    `Completed by: ${projectInfo.completedBy ?? '—'}`,
  ];
  if (cls) {
    clsLines.push(`Classification: CLASS ${cls.selected} (recommended ${cls.recommended})`);
    cls.triggers.forEach((t) => clsLines.push(`  Trigger ${t.rule}: ${t.detail}`));
    if (cls.exception.invoked) {
      clsLines.push(
        `Exception invoked: §${cls.exception.invoked} — QE engaged: ${cls.exception.qeEngaged ? 'yes' : 'no'}`
      );
    }
    if (cls.override) {
      clsLines.push(
        `Override by ${cls.override.by} at ${cls.override.timestamp}: ${cls.override.justification}`
      );
    }
    if (cls.weightBandNote) {
      clsLines.push(`Note: ${cls.weightBandNote}`);
    }
    clsLines.push('Citation: A10.48-2023 §4.4 (classification), §4.4.4.1 (exceptions)');
  } else {
    clsLines.push('Classification: UNCLASSIFIED (legacy plan — run the classification wizard)');
  }
  sections.push({ kind: 'classification', heading: 'CLASSIFICATION', lines: clsLines });

  // Plan considerations (§4.4.5.1) — ticked items recorded by the QP
  const considered = plan.planConsiderations;
  if (Array.isArray(considered) && considered.length > 0) {
    sections.push({
      kind: 'compliance',
      heading: 'PLAN CONSIDERATIONS (§4.4.5.1)',
      lines: [
        `Considered and documented (${considered.length} of 14 checked by QP):`,
        ...considered.map((c) => `  ✓ ${String(c)}`),
      ],
    });
  }

  // 2 — Personnel (§1.6 rule 2) — a safety document never reads as an
  // omission: empty sections print an explicit "(not provided)" line (audit L-4).
  const p = plan.personnel;
  const personnelLines: string[] = [];
  if (p) {
    if (p.qualifiedPerson) personnelLines.push(`Qualified Person: ${p.qualifiedPerson}`);
    if (p.competentRigger) personnelLines.push(`Competent Rigger: ${p.competentRigger}`);
    p.onsiteCompetentPersons.forEach((n) => personnelLines.push(`Onsite Competent Person: ${n}`));
    if (p.qualifiedEngineer) {
      personnelLines.push(
        `Qualified Engineer (engaged reviewer — never author): ${p.qualifiedEngineer.name}, ${p.qualifiedEngineer.firm} (TIA report ${p.qualifiedEngineer.tiaReportRef})`
      );
    }
  }
  if (!personnelLines.length) personnelLines.push('(not provided)');
  sections.push({ kind: 'personnel', heading: 'PERSONNEL (§1.6)', lines: personnelLines });

  // 3 — Stakeholders (§4.3.1–4.3.5)
  const s = plan.stakeholders;
  const stakeLines: string[] = [];
  if (s) {
    if (s.ownerClient) stakeLines.push(`Owner/Client: ${s.ownerClient}`);
    if (s.structureOwner) stakeLines.push(`Structure Owner: ${s.structureOwner}`);
    (s.fccLicensedEntities ?? []).forEach((e) => stakeLines.push(`FCC-Licensed Entity: ${e}`));
    if (s.multiEmployer) {
      stakeLines.push(
        `Multi-employer — creating: ${s.multiEmployer.creating}; exposing: ${s.multiEmployer.exposing}; ` +
          `correcting: ${s.multiEmployer.correcting}; controlling: ${s.multiEmployer.controlling}`
      );
    }
  }
  if (!stakeLines.length) stakeLines.push('(not provided)');
  sections.push({ kind: 'stakeholders', heading: 'STAKEHOLDERS & COMMUNICATIONS (§4.3)', lines: stakeLines });

  // 4 — Lift details: per lift card → shown-work (ADJACENT) → diagram at scale
  plan.liftDetails.forEach((lift) => {
    const computed = lift.computed;
    sections.push({
      kind: 'lift',
      heading: `LIFT — ${lift.name}`,
      lines: liftLines(lift, computed),
      liftId: lift.id,
    });
    sections.push({
      kind: 'shown-work',
      heading: `SHOWN WORK — ${lift.name}`,
      lines: shownWorkLines(computed),
      liftId: lift.id,
    });
    sections.push({
      kind: 'diagram',
      heading: `DIAGRAM (TRUE SCALE) — ${lift.name}`,
      lines: [
        'Legend:',
        ...DIAGRAM_LEGEND.map((l) => `  ${l}`),
        'Scale: single pxPerFt factor for BOTH axes (no vertical exaggeration).',
        'Angles measured from vertical (A-6 convention).',
      ],
      liftId: lift.id,
    });
  });

  // 5 — Compliance steps (incl. proof-test log per F-3)
  const complianceLines: string[] = [];
  (plan.complianceSteps ?? []).forEach((step) => {
    complianceLines.push(`[${step.status.toUpperCase()}] ${step.ref}: ${step.requirement}`);
    if (step.evidence) complianceLines.push(`  Evidence: ${step.evidence} (by ${step.by ?? '—'} at ${step.at ?? '—'})`);
  });
  sections.push({ kind: 'compliance', heading: 'COMPLIANCE STEPS (§6)', lines: complianceLines });

  // 6 — Proof-test log (C-4b / §9.6 — F-4 audit round 1 HIGH fix). Table-style
  // lines per record: config label, location, test load (1.25× pre-fill),
  // device + ID, hold time, EXIF-confirmed photo timestamps, inspector,
  // attestation, status. Photos: filenames listed (the browser-render path
  // embeds captured thumbnails when available; export never blocks on them).
  const proofSections: PlanPdfSection[] = (plan.proofTests ?? [])
    .filter((r): r is ProofTestRecord => isRecordLike(r))
    .map((r) => {
      const lines: string[] = [];
      lines.push(`Configuration: ${r.configLabel || '—'} · Location: ${r.location || '—'}`);
      lines.push(
        `Test load: ${r.testLoadLbs != null ? `${r.testLoadLbs.toFixed(1)} lb` : '—'}` +
          (r.maxAnticipatedLoadLbs != null ? ` (1.25 × ${r.maxAnticipatedLoadLbs.toFixed(1)} lb max anticipated)` : '')
      );
      lines.push(`Hold: ${r.holdMinutes} min (≥ 5 required) · Device: ${r.device || '—'}${r.deviceId ? ` [${r.deviceId}]` : ''}`);
      lines.push(
        `Attestation — no personnel on structure during test: ${r.attestationNoPersonnel ? 'YES' : 'NO'}`
      );
      lines.push(`Post-test inspection: ${r.postTestInspector ?? '—'}`);
      const photos = (r.photos ?? []).map(
        (ph) =>
          `  ${ph.name}${ph.exifTimestamp ? ` — EXIF ${ph.exifTimestamp}${ph.exifConfirmedByUser ? ' (user-confirmed)' : ''}` : ''}`
      );
      if (photos.length) {
        lines.push(`Evidence photos (${photos.length}):`);
        lines.push(...photos);
      } else {
        lines.push('Evidence photos: none uploaded');
      }
      if (r.notes) lines.push(`Notes: ${r.notes}`);
      lines.push(`Status: ${r.status.toUpperCase()}${r.documentedAt ? ` at ${r.documentedAt}` : ''}`);
      return { kind: 'proof-test' as const, heading: `PROOF-TEST LOG (§9.6) — ${r.configLabel || r.location || r.id}`, lines };
    });
  sections.push(...proofSections);

  // 7 — Crane-lift-plan attachment pages with the §7.3 cover note
  (plan.attachments ?? [])
    .filter((a) => a.type === 'craneLiftPlan')
    .forEach((a) => {
      sections.push({
        kind: 'attachment',
        heading: 'APPENDED — CRANE LIFT PLAN (§8.3)',
        lines: [attachmentCoverNote(a)],
      });
    });

  return sections;
}

// ---------------------------------------------------------------------------
// jsPDF rendering (browser-only; dynamically imported so jsdom tests skip it)
// ---------------------------------------------------------------------------
const PAGE_W = 8.5;
const PAGE_H = 11;
const MARGIN = 0.75;
const FOOTER_REFS_DEFAULT = ['§4.4', '§4.4.4.1', '§9.6', 'Appendix A-13'];

function footer(doc: import('jspdf').jsPDF, refs: string[]): void {
  const citation = `${PDF_FOOTER_CITATION}${refs.length ? ` — ${refs.join(', ')}` : ''}`;
  doc.setFontSize(8);
  doc.setTextColor(90);
  doc.text(citation, MARGIN, PAGE_H - 0.4);
  doc.text(
    `Page ${doc.getNumberOfPages()}`,
    PAGE_W - MARGIN,
    PAGE_H - 0.4,
    { align: 'right' }
  );
  doc.setTextColor(0);
}

/** Capture a live LiftDiagramPanel SVG as a PNG data URL via html2canvas.
 *  Background is derived from the panel's ACTUAL computed background-color
 *  (F-4 audit round 1 MED-a) so dark mode doesn't render light-on-white. */
async function captureDiagram(liftId: string): Promise<string | null> {
  const el = document.querySelector<HTMLElement>(`[data-lift-id="${liftId}"] .lift-diagram-panel svg`)?.parentElement
    ?? document.querySelector<HTMLElement>(`[data-lift-id="${liftId}"] .lift-diagram-panel`);
  if (!el) return null;
  const backgroundColor = getComputedStyle(el).backgroundColor || '#ffffff';
  try {
    const html2canvas = (await import('html2canvas')).default;
    const canvas = await html2canvas(el, { backgroundColor, scale: 2 });
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

// F-4 audit round 1 MED — diagram capture cap: bounds the data-URL memory Map
// for large plans. Lifts beyond the cap print the omission line instead.
export const MAX_DIAGRAM_CAPTURES = 20;

export async function exportPlanPdf(
  input: PlanPdfInput,
  fileName?: string,
): Promise<void> {
  // CRITICAL invariant (F-4 audit round 1): the exporter itself re-enforces
  // the fresh-recompute guarantee — even a caller that skips
  // recomputePlanForExport cannot print migrated/stored zero-forces caches.
  const plan = recomputePlanForExport(input.plan);
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'in', format: 'letter' });
  const refs = input.refs ?? FOOTER_REFS_DEFAULT;
  const sections = buildPlanPdfSections({ ...input, plan });
  const diagramImages = new Map<string, string | null>();
  const diagramSections = sections.filter((s) => s.kind === 'diagram' && s.liftId);
  for (const [i, s] of diagramSections.entries()) {
    diagramImages.set(s.liftId!, i < MAX_DIAGRAM_CAPTURES ? await captureDiagram(s.liftId!) : null);
  }
  const omittedDiagrams = Math.max(diagramSections.length - MAX_DIAGRAM_CAPTURES, 0);

  let y = MARGIN;
  const ensureSpace = (needed: number) => {
    if (y + needed > PAGE_H - MARGIN - 0.3) {
      footer(doc, refs);
      doc.addPage();
      y = MARGIN;
    }
  };
  const writeLines = (lines: string[], indent = 0) => {
    const x = MARGIN + 0.15 + indent * 0.2;
    const maxW = PAGE_W - x - MARGIN;
    lines.forEach((line) => {
      doc.setFontSize(9);
      const wrapped = doc.splitTextToSize(line || ' ', maxW) as string[];
      wrapped.forEach((seg) => {
        ensureSpace(0.18);
        doc.text(seg, x, y);
        y += 0.16;
      });
    });
  };

  sections.forEach((section) => {
    if (section.kind === 'title') {
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      ensureSpace(0.9);
      doc.text(section.heading, MARGIN, y);
      y += 0.28;
      doc.setFont('helvetica', 'normal');
      writeLines(section.lines);
      y += 0.1;
      return;
    }
    ensureSpace(0.5);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(section.heading, MARGIN, y);
    y += 0.08;
    doc.setDrawColor(180);
    doc.line(MARGIN, y, PAGE_W - MARGIN, y);
    y += 0.14;
    doc.setFont('helvetica', 'normal');

    if (section.kind === 'diagram' && section.liftId) {
      writeLines(section.lines);
      const img = diagramImages.get(section.liftId);
      if (img) {
        ensureSpace(4.6);
        doc.addImage(img, 'PNG', MARGIN, y, 6.0, 4.5);
        y += 4.7;
      } else if (section === diagramSections[MAX_DIAGRAM_CAPTURES - 1] && omittedDiagrams > 0) {
        writeLines(
          [`(Diagram capture omitted (${omittedDiagrams} more) — see the live plan view.)`],
          1
        );
      } else {
        writeLines(['(Diagram capture unavailable — see the live plan view.)'], 1);
      }
      y += 0.1;
      return;
    }

    writeLines(section.lines);
    y += 0.14;
  });

  footer(doc, refs);
  const name =
    fileName ??
    `${String(input.plan.projectInfo?.projectName ?? 'construction-plan').replace(/[^\w-]+/g, '_')}.pdf`;
  doc.save(name);
}
